// pages/admin/AdminPartners.jsx — liste des partenaires (comités
// d'entreprise, offices de tourisme, etc.), chacun avec sa propre remise
// (% ou montant fixe) configurée. La remise se déclenche ensuite depuis une
// réservation (voir BookingDetail.jsx), par n'importe quel membre du staff —
// seule la GESTION de la liste (créer/modifier/désactiver/supprimer) est
// réservée aux managers/admins ici.
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../utils/auth';
import { partnersApi } from '../../utils/api';
import { Percent, Plus, Pencil, Trash2, X } from 'lucide-react';
import toast from 'react-hot-toast';

const EMPTY_FORM = { name: '', discountType: 'percent', discountValue: '', notes: '' };

export default function AdminPartners() {
  const { can } = useAuth();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = fermé, {} = nouveau, {...} = édition
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    partnersApi.list()
      .then(r => setPartners(r.data.partners || []))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!can('manager')) return (
    <div className="page-body"><div className="empty-state card"><p>Accès non autorisé</p></div></div>
  );

  const openNew = () => { setForm(EMPTY_FORM); setEditing({}); };
  const openEdit = (p) => {
    setForm({ name: p.name, discountType: p.discount_type, discountValue: p.discount_value, notes: p.notes || '' });
    setEditing(p);
  };
  const close = () => setEditing(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Nom requis');
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        discountType: form.discountType,
        discountValue: parseFloat(form.discountValue) || 0,
        notes: form.notes || null,
      };
      if (editing?.id) {
        await partnersApi.update(editing.id, payload);
        toast.success('Partenaire modifié');
      } else {
        await partnersApi.create(payload);
        toast.success('Partenaire ajouté');
      }
      close();
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (p) => {
    try {
      await partnersApi.update(p.id, { active: !p.active });
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer le partenaire "${p.name}" ?`)) return;
    try {
      await partnersApi.remove(p.id);
      toast.success('Partenaire supprimé');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    }
  };

  return (
    <>
      <div className="page-header">
        <div>
          <h2>Partenaires</h2>
          <p>Remises négociées, activables depuis une réservation</p>
        </div>
        <button className="btn btn-primary" onClick={openNew}>
          <Plus size={16} /> Nouveau partenaire
        </button>
      </div>

      <div className="page-body" style={{ display: 'grid', gap: 16, maxWidth: 720 }}>
        {editing && (
          <form className="card" onSubmit={submit}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>
                {editing.id ? 'Modifier le partenaire' : 'Nouveau partenaire'}
              </h3>
              <button type="button" onClick={close} className="btn-icon"><X size={16} /></button>
            </div>

            <div className="form-group">
              <label className="form-label">Nom</label>
              <input className="input" value={form.name} onChange={e => set('name', e.target.value)}
                placeholder="Ex : Comité d'entreprise X" />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Type de remise</label>
                <select className="input" value={form.discountType} onChange={e => set('discountType', e.target.value)}>
                  <option value="percent">Pourcentage (%)</option>
                  <option value="fixed">Montant fixe (€)</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{form.discountType === 'percent' ? 'Réduction (%)' : 'Réduction (€)'}</label>
                <input type="number" step="0.01" min="0" className="input" value={form.discountValue}
                  onChange={e => set('discountValue', e.target.value)} placeholder="0" />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Notes (optionnel)</label>
              <input className="input" value={form.notes} onChange={e => set('notes', e.target.value)}
                placeholder="Ex : accord signé le..." />
            </div>

            <div className="modal-footer" style={{ justifyContent: 'flex-end' }}>
              <button className="btn btn-primary" type="submit" disabled={saving}>
                {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Enregistrer'}
              </button>
            </div>
          </form>
        )}

        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <Percent size={15} />
            <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>Liste des partenaires</h3>
          </div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 20 }}><span className="spinner" /></div>
          ) : partners.length === 0 ? (
            <p style={{ fontSize: 12, color: 'var(--text-3)' }}>Aucun partenaire pour le moment</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {partners.map(p => (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 12px', background: 'var(--bg-elevated)', borderRadius: 8,
                  opacity: p.active ? 1 : 0.5 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{p.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                      -{p.discount_value}{p.discount_type === 'percent' ? '%' : '€'}
                      {!p.active ? ' · désactivé' : ''}
                      {p.notes ? ` · ${p.notes}` : ''}
                    </div>
                  </div>
                  <button type="button" className="btn btn-sm" onClick={() => toggleActive(p)}>
                    {p.active ? 'Désactiver' : 'Activer'}
                  </button>
                  <button type="button" className="btn-icon" onClick={() => openEdit(p)}><Pencil size={14} /></button>
                  <button type="button" className="btn-icon" onClick={() => remove(p)}><Trash2 size={14} color="var(--red)" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
